import React, { useState, useEffect } from 'react';
import clsx from 'clsx';
import { Music, ExternalLink, Trash2, Loader2, CheckCircle2, Clock, Sparkles } from 'lucide-react';
import { getToken } from '@/lib/auth';

interface GeneratedPlaylist {
  id: number;
  name: string;
  description?: string;
  engine_used: string;
  track_count: number;
  is_synced_to_spotify: boolean;
  spotify_playlist_id?: string;
  created_at: string;
  synced_at?: string;
}

interface GeneratedPlaylistsListProps {
  refreshTrigger?: number;
}

const ENGINE_LABELS: Record<string, string> = {
  lastfm: 'Last.fm Collaborative',
  hybrid: 'Hybrid AI',
  mood: 'Mood-Based',
  auralist: 'Auralist LDA',
  artist_radio: 'Artist Radio',
};

/**
 * GeneratedPlaylistsList Component
 *
 * Displays user's generated playlists with management options.
 * Features:
 * - List view with metadata (engine, track count, sync status)
 * - "Sync to Spotify" button for unsynced playlists
 * - Open in Spotify button for synced playlists
 * - Delete functionality
 * - Auto-refresh on generation complete
 */
export const GeneratedPlaylistsList: React.FC<GeneratedPlaylistsListProps> = ({
  refreshTrigger = 0,
}) => {
  const [playlists, setPlaylists] = useState<GeneratedPlaylist[]>([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState<number | null>(null);
  const [deleting, setDeleting] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchPlaylists();
  }, [refreshTrigger]);

  const fetchPlaylists = async () => {
    setLoading(true);
    setError(null);
    try {
      const token = getToken();
      const response = await fetch('/automusic/api/playlists/generated', {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch playlists');
      }

      const data = await response.json();
      setPlaylists(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load playlists');
    } finally {
      setLoading(false);
    }
  };

  const handleSync = async (playlistId: number) => {
    setSyncing(playlistId);
    setError(null);

    try {
      const token = getToken();
      const response = await fetch('/automusic/api/playlists/sync', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ playlist_id: playlistId }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to sync playlist');
      }

      // Refresh the list
      await fetchPlaylists();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to sync playlist');
    } finally {
      setSyncing(null);
    }
  };

  const handleDelete = async (playlistId: number, playlistName: string) => {
    if (!confirm(`Delete "${playlistName}"? This cannot be undone.`)) {
      return;
    }

    setDeleting(playlistId);
    setError(null);

    try {
      const token = getToken();
      const response = await fetch(`/automusic/api/playlists/${playlistId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to delete playlist');
      }

      // Remove from local state
      setPlaylists(prev => prev.filter(p => p.id !== playlistId));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete playlist');
    } finally {
      setDeleting(null);
    }
  };

  const getSpotifyUrl = (playlistId: string) => {
    return `https://open.spotify.com/playlist/${playlistId}`;
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="animate-spin text-spotify-green" size={48} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-900 bg-opacity-30 border border-red-500 rounded-lg p-4">
        <p className="text-red-400">{error}</p>
        <button
          onClick={fetchPlaylists}
          className="text-red-300 hover:underline mt-2"
        >
          Try Again
        </button>
      </div>
    );
  }

  if (playlists.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 bg-gray-900 rounded-lg">
        <Music className="text-gray-600 mb-4" size={64} />
        <h3 className="text-xl font-semibold text-white mb-2">No Generated Playlists Yet</h3>
        <p className="text-gray-400 text-center mb-4">
          Create your first playlist to see it here
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <h2 className="text-2xl font-bold text-white mb-4">Your Generated Playlists</h2>

      {playlists.map((playlist) => (
        <div
          key={playlist.id}
          className="bg-gray-900 rounded-lg p-6 hover:bg-gray-850 transition"
        >
          <div className="flex items-start justify-between">
            {/* Playlist Info */}
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-2">
                <h3 className="text-xl font-semibold text-white">{playlist.name}</h3>
                {playlist.is_synced_to_spotify && (
                  <span className="flex items-center gap-1 px-2 py-1 bg-green-900 bg-opacity-30 border border-green-500 rounded-full text-green-400 text-xs">
                    <CheckCircle2 size={12} />
                    Synced
                  </span>
                )}
              </div>

              {playlist.description && (
                <p className="text-gray-400 text-sm mb-3">{playlist.description}</p>
              )}

              <div className="flex flex-wrap gap-4 text-sm text-gray-500">
                <span className="flex items-center gap-1">
                  <Sparkles size={14} className="text-spotify-green" />
                  {ENGINE_LABELS[playlist.engine_used] || playlist.engine_used}
                </span>
                <span className="flex items-center gap-1">
                  <Music size={14} />
                  {playlist.track_count} tracks
                </span>
                <span className="flex items-center gap-1">
                  <Clock size={14} />
                  Created {formatDate(playlist.created_at)}
                </span>
                {playlist.synced_at && (
                  <span className="flex items-center gap-1">
                    <CheckCircle2 size={14} />
                    Synced {formatDate(playlist.synced_at)}
                  </span>
                )}
              </div>
            </div>

            {/* Actions */}
            <div className="flex items-center gap-2 ml-4">
              {playlist.is_synced_to_spotify && playlist.spotify_playlist_id ? (
                <a
                  href={getSpotifyUrl(playlist.spotify_playlist_id)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 px-4 py-2 bg-spotify-green text-black rounded-lg hover:bg-green-400 transition font-semibold"
                >
                  <ExternalLink size={16} />
                  Open in Spotify
                </a>
              ) : (
                <button
                  onClick={() => handleSync(playlist.id)}
                  disabled={syncing === playlist.id}
                  className={clsx(
                    'flex items-center gap-2 px-4 py-2 rounded-lg font-semibold transition',
                    {
                      'bg-spotify-green text-black hover:bg-green-400': syncing !== playlist.id,
                      'bg-gray-700 text-gray-500 cursor-not-allowed': syncing === playlist.id,
                    }
                  )}
                >
                  {syncing === playlist.id ? (
                    <>
                      <Loader2 className="animate-spin" size={16} />
                      Syncing...
                    </>
                  ) : (
                    <>
                      <CheckCircle2 size={16} />
                      Sync to Spotify
                    </>
                  )}
                </button>
              )}

              <button
                onClick={() => handleDelete(playlist.id, playlist.name)}
                disabled={deleting === playlist.id}
                className="p-2 text-gray-400 hover:text-red-400 transition"
                title="Delete playlist"
              >
                {deleting === playlist.id ? (
                  <Loader2 className="animate-spin" size={20} />
                ) : (
                  <Trash2 size={20} />
                )}
              </button>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

export default GeneratedPlaylistsList;
